import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Alert, Button, Icon, Intent, Menu, MenuDivider, MenuItem, Popover, Position } from '@blueprintjs/core';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Toast from 'helpers/toaster';
import { formatTime } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import { reason } from 'components/DocEditor/DocModel';
import { documentTotals } from 'components/DocEditor/lineMath';

// Pieces shared by the work order, estimate and invoice editors
// (styles: document-editor.scss, de-).

export const money = (value) => currencyFormattedAmount(Number.isFinite(value) ? value : 0);

// The toolbar above the sheet: status on the left, actions on the right.
export const Toolbar = ({ children, actions }) => (
  <div className="de-toolbar">
    <div className="de-toolbar__status">{children}</div>
    <div className="de-toolbar__actions">{actions}</div>
  </div>
);

// Blueprint's Popover hands its target a className of its own; keep ours too.
export const ToolButton = ({ icon, children, primary, busy, className = '', ...rest }) => (
  <button type="button" {...rest} className={`de-tool${primary ? ' de-tool--primary' : ''} ${className}`.trim()} disabled={busy || rest.disabled} aria-busy={busy || undefined}>
    {icon && <Icon icon={busy ? 'time' : icon} iconSize={14} />}
    {children}
  </button>
);

// "More" menu: items are { text, icon, onClick, href, disabled, divider }.
export const MoreMenu = ({ items }) => (
  <Popover
    position={Position.BOTTOM_RIGHT}
    minimal
    popoverClassName="de-menu-popover"
    content={(
      <Menu className="de-menu">
        {items.filter(Boolean).map((item, index) => (item.divider
          ? <MenuDivider key={`divider-${index}`} title={item.title} />
          : (
            <MenuItem
              key={item.text}
              icon={item.icon}
              text={item.text}
              href={item.href}
              target={item.href ? '_blank' : undefined}
              disabled={item.disabled}
              onClick={item.onClick}
              labelElement={item.label}
            />
          )))}
      </Menu>
    )}
  >
    <ToolButton icon="more">More</ToolButton>
  </Popover>
);

// The record's status as a select dressed as its pill.
export const StatusSelect = ({ value, statuses, onChange, disabled, label = 'Status' }) => {
  const current = statuses.find((status) => status.value === value);
  return (
    <span className="de-status-field">
      <label className="de-status-field__label" htmlFor="de-status">{label}</label>
      <select
        id="de-status"
        className={`de-status de-status--${current ? current.tone : 'grey'}`}
        value={current ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {!current && <option value="" disabled>{value ? value : 'Choose a status'}</option>}
        {statuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
      </select>
    </span>
  );
};

export const Letterhead = ({ tenant = {} }) => {
  const place = [tenant.city, tenant.province, tenant.postal_code].filter(Boolean).join(', ');
  return (
    <header className="de-letterhead">
      <div className="de-letterhead__logo">
        {tenant.logo ? <img src={tenant.logo} alt={tenant.name || 'Company logo'} /> : <span className="de-letterhead__name">{tenant.name}</span>}
      </div>
      <address className="de-letterhead__address">
        {tenant.logo && tenant.name && <strong>{tenant.name}</strong>}
        {tenant.street_address && <span>{tenant.street_address}</span>}
        {tenant.street_address_other && <span>{tenant.street_address_other}</span>}
        {place && <span>{place}</span>}
        {tenant.phone && <span>{tenant.phone}</span>}
        {tenant.tax_number_title && tenant.tax_number && <span>{tenant.tax_number_title}: {tenant.tax_number}</span>}
      </address>
    </header>
  );
};

// "Work order" #1042, stamped with where it's at.
export const DocTitle = ({ kind, number, stamp }) => (
  <div className="de-title-row">
    <h1 className="de-title">
      {kind}
      {number && <span className="de-title__number">{number}</span>}
    </h1>
    {stamp && <div className={`de-stamp de-stamp--${stamp.tone}`} role="img" aria-label={`Status: ${stamp.label}`}>{stamp.label}</div>}
  </div>
);

// Who it's for: name (to their page), address, how to reach them.
export const ClientBlock = ({ label = 'For', client = {}, address, href, children }) => (
  <section className="de-party" aria-label={label}>
    <h2 className="de-label">{label}</h2>
    {client.name && (href ? <a className="de-party__name" href={href}>{client.name}</a> : <span className="de-party__name">{client.name}</span>)}
    {address && <span>{address}</span>}
    {client.phone && <a href={`tel:${client.phone}`}>{client.phone}</a>}
    {client.email && <a href={`mailto:${client.email}`}>{client.email}</a>}
    {children}
  </section>
);

// A label and its value or field in the details beside the client.
export const Meta = ({ label, htmlFor, children }) => (
  <div className="de-meta__item">
    {htmlFor ? <label className="de-label" htmlFor={htmlFor}>{label}</label> : <span className="de-label">{label}</span>}
    <div className="de-meta__value">{children}</div>
  </div>
);

// A total that pulses gently when it changes, so you see your edit land.
export const LiveAmount = ({ value, className = '' }) => (
  <span key={money(value)} className={`de-live ${className}`}>{money(value)}</span>
);

// Where the money is: labor, materials and other as a share of the subtotal
// (a bar in the groups' colours, then each with its amount and share).
const PARTS = [
  { key: 'labour', label: 'Labor', icon: 'time' },
  { key: 'materials', label: 'Materials', icon: 'box' },
  { key: 'custom_line_items', label: 'Other', icon: 'tag' },
];

export const Breakdown = observer(({ doc }) => {
  const totals = documentTotals(doc.attributes);
  const whole = PARTS.reduce((sum, part) => sum + Math.max(totals.groups[part.key], 0), 0);
  const share = (value) => (whole > 0 ? Math.round((Math.max(value, 0) / whole) * 100) : 0);
  return (
    <section className="de-breakdown" aria-label="Breakdown">
      <div className="de-breakdown__bar" aria-hidden="true">
        {whole > 0
          ? PARTS.filter((part) => totals.groups[part.key] > 0).map((part) => (
            <span key={part.key} className={`de-breakdown__segment de-part--${part.key}`} style={{ flexGrow: totals.groups[part.key] }} />
          ))
          : <span className="de-breakdown__segment is-empty" />}
      </div>
      <dl className="de-breakdown__items">
        {PARTS.map((part) => {
          const value = totals.groups[part.key];
          return (
            <div key={part.key} className={`de-breakdown__item de-part--${part.key}${value ? '' : ' is-zero'}`}>
              <dt><span className="de-breakdown__dot" aria-hidden="true" />{part.label}</dt>
              <dd>{money(value)}</dd>
              <span className="de-breakdown__share">{share(value)}%</span>
            </div>
          );
        })}
      </dl>
    </section>
  );
});

// The whole document before and after markup (for your team; customers don't
// see it): before markup, gross markup and gross margin.
export const MarkupNote = ({ totals }) => (
  <dl className="de-markup" aria-label="Markup">
    <div><dt>Before markup</dt><dd>{money(totals.cost)}</dd></div>
    <div><dt>Gross markup</dt><dd>{totals.markupPercent === null ? '–' : `${totals.markupPercent}%`}</dd></div>
    <div><dt>Gross margin</dt><dd>{money(totals.margin)}</dd></div>
  </dl>
);

// The figures under the lines: rows of { label, value, strong, muted }.
export const Totals = observer(({ rows, note }) => (
  <div className="de-totals">
    {note && <div className="de-totals__note">{note}</div>}
    <dl className="de-totals__rows">
      {rows.filter(Boolean).map((row) => (
        <div key={row.label} className={`de-totals__row${row.strong ? ' is-strong' : ''}${row.muted ? ' is-muted' : ''}`}>
          <dt>{row.label}</dt>
          <dd>{row.strong ? <LiveAmount value={row.value} /> : (row.text || money(row.value))}</dd>
        </div>
      ))}
    </dl>
  </div>
));

// The figures in the bar at the foot of the screen, always in view: rows of
// { label, value, text, strong } built from the record's totals.
export const BarFigures = observer(({ doc, build }) => (
  <dl className="de-barfigures">
    {build(documentTotals(doc.attributes), doc).filter(Boolean).map((row) => (
      <div key={row.label} className={row.strong ? 'is-strong' : ''}>
        <dt>{row.label}</dt>
        <dd>{row.strong ? <LiveAmount value={row.value} /> : (row.text || money(row.value))}</dd>
      </div>
    ))}
  </dl>
));

// Totals that work themselves out from the record, so typing in a line only
// re-renders the line and these, not the whole editor. `build(totals, doc)`
// returns Totals' props ({ rows, note }).
export const LiveTotals = observer(({ doc, build }) => <Totals {...build(documentTotals(doc.attributes), doc)} />);

// A titled section on the sheet (description, notes, signatures).
export const SheetSection = ({ title, aside, children, className = '' }) => (
  <section className={`de-section ${className}`}>
    {(title || aside) && (
      <header className="de-section__head">
        {title && <h2 className="de-section__title">{title}</h2>}
        {aside}
      </header>
    )}
    {children}
  </section>
);

// What goes with the record but isn't on it (files, forms, payments, notes
// for the team…): SideCards inside a SidePanel, shown as tabs in a panel
// beside the sheet on wide screens, below it otherwise.
const PanelContext = React.createContext(false);

export const SideCard = ({ title, count, action: cardAction, children }) => {
  const inPanel = React.useContext(PanelContext);
  if (inPanel) {
    return (
      <section className="de-pane" aria-label={title}>
        {cardAction && <div className="de-pane__action">{cardAction}</div>}
        {children}
      </section>
    );
  }
  return (
    <section className="de-card">
      <header className="de-card__head">
        <h2 className="de-card__title">
          {title}
          {count > 0 && <span className="de-card__count">{count}</span>}
        </h2>
        {cardAction}
      </header>
      {children}
    </section>
  );
};

// The panel: one tab per SideCard child (its title and count), the last tab
// used remembered per kind of record.
@observer
export class SidePanel extends React.Component {
  @observable chosen = null;

  storageKey() {
    return `de-panel-tab:${this.props.kind}`;
  }

  componentDidMount() {
    try {
      this.chosen = window.localStorage.getItem(this.storageKey());
    } catch (error) {
      this.chosen = null;
    }
  }

  choose(title) {
    this.chosen = title;
    try {
      window.localStorage.setItem(this.storageKey(), title);
    } catch (error) {
      // Not remembered; fine.
    }
  }

  render() {
    const cards = React.Children.toArray(this.props.children).filter(Boolean);
    if (cards.length === 0) return null;
    const active = cards.find((card) => card.props.title === this.chosen) || cards[0];
    const single = cards.length === 1;
    return (
      <aside className="de-panel" aria-label="With this record">
        {single ? (
          <h2 className="de-panel__title">
            {active.props.title}
            {active.props.count > 0 && <span className="de-card__count">{active.props.count}</span>}
          </h2>
        ) : (
          <div className="de-tabs" role="tablist" aria-label="With this record">
            {cards.map((card) => {
              const selected = card === active;
              return (
                <button
                  key={card.props.title}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`de-tab${selected ? ' is-active' : ''}`}
                  onClick={() => this.choose(card.props.title)}
                >
                  {card.props.title}
                  {card.props.count > 0 && <span className="de-tab__count">{card.props.count}</span>}
                </button>
              );
            })}
          </div>
        )}
        <div className="de-panel__body" role={single ? undefined : 'tabpanel'}>
          <PanelContext.Provider value={true}>{active}</PanelContext.Provider>
        </div>
      </aside>
    );
  }
}

// A record's notes in one section with a tab per note (notes for the
// customer, payment terms, agreement, private notes…). A tab with something
// in it has a dot; the private one is marked as internal; the last tab used
// is remembered per kind of record. tabs: [{ field, label, placeholder,
// private, note (who sees it), hidden }].
const hasText = (html) => Boolean(String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim());

@observer
export class NotesTabs extends React.Component {
  @observable chosen = null;

  storageKey() {
    return `de-notes-tab:${this.props.kind}`;
  }

  componentDidMount() {
    try {
      this.chosen = window.localStorage.getItem(this.storageKey());
    } catch (error) {
      this.chosen = null;
    }
  }

  choose(field) {
    this.chosen = field;
    try {
      window.localStorage.setItem(this.storageKey(), field);
    } catch (error) {
      // Not remembered; fine.
    }
  }

  render() {
    const { tabs, attributes, RichTextEditor } = this.props;
    const active = tabs.find((tab) => tab.field === this.chosen) || tabs[0];
    return (
      <section className={`de-section de-notes${active.private ? ' is-private' : ''}`} aria-label="Notes">
        <div className="de-notes__tabs" role="tablist" aria-label="Notes">
          {tabs.map((tab) => {
            const selected = tab === active;
            return (
              <button
                key={tab.field}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`de-notes__tab${selected ? ' is-active' : ''}${tab.private ? ' is-private' : ''}`}
                onClick={() => this.choose(tab.field)}
              >
                {tab.private && <Icon icon="lock" iconSize={11} />}
                {tab.label}
                {hasText(attributes[tab.field]) && <span className="de-notes__dot" aria-label="has notes" />}
              </button>
            );
          })}
        </div>
        <div className="de-notes__panel" role="tabpanel">
          <RichTextEditor
            key={active.field}
            label={active.label}
            value={attributes[active.field] || ''}
            placeholder={active.placeholder}
            onChange={(html) => { attributes[active.field] = html; }}
          />
          {/* Every tab has one line here, so switching tabs doesn't move anything. */}
          <div className="de-notes__aside">
            {active.private
              ? <span className="de-private__badge"><Icon icon="lock" iconSize={11} /> Only your team sees these</span>
              : <span className="de-notes__shown"><Icon icon={active.hidden ? 'eye-off' : 'eye-open'} iconSize={11} /> {active.note}</span>}
          </div>
        </div>
      </section>
    );
  }
}

// Notes only the team sees, on the paper but marked as internal.
export const PrivateNotes = observer(({ attributes, RichTextEditor }) => (
  <section className="de-section de-private" aria-label="Private notes">
    <header className="de-section__head">
      <h2 className="de-section__title">Private notes</h2>
      <span className="de-private__badge"><Icon icon="lock" iconSize={11} /> Only your team sees these</span>
    </header>
    <RichTextEditor label="Private notes" value={attributes.private_notes || ''} placeholder="Notes for your team: access codes, follow-ups, anything the customer shouldn't see" onChange={(html) => { attributes.private_notes = html; }} />
  </section>
));

// Signatures captured on the record (read-only).
export const Signatures = ({ signatures = {}, accepted }) => {
  const client = signatures && signatures.client_signature;
  const tech = signatures && signatures.tech_signature;
  if (!client && !tech) return <p className="de-muted">No signatures yet.</p>;
  return (
    <div className="de-signatures">
      {client && (
        <figure>
          <img src={client.asset && (client.asset.thumb ? client.asset.thumb.url : client.asset.url)} alt="Customer signature" />
          <figcaption>Customer{accepted}</figcaption>
        </figure>
      )}
      {tech && (
        <figure>
          <img src={tech.asset && (tech.asset.thumb ? tech.asset.thumb.url : tech.asset.url)} alt="Technician signature" />
          <figcaption>Technician</figcaption>
        </figure>
      )}
    </div>
  );
};

// Files on the record, one per row: a thumbnail (images open full size) or the
// file type's icon (others open in a new tab), the name and when it was added.
// Add several at once, delete behind a prompt. `doc` provides
// uploadFiles(files) and removeFile(file).
const IMAGE = /\.(jpe?g|png|gif|svg|webp)(\?|$)/i;
export const FILE_ACCEPT = '.jpg,.jpeg,.gif,.png,.pdf,.doc,.docx,.xls,.xlsx,.svg,.md,.txt,.csv,.odt,.ppt,.pptx,.rtf';

// The app's file-type icons (src/images), by extension.
const TYPE_ICONS = {
  pdf: require('../../images/pdf.png'),
  doc: require('../../images/doc.png'),
  docx: require('../../images/doc.png'),
  xls: require('../../images/xls.png'),
  xlsx: require('../../images/xls.png'),
  csv: require('../../images/csv.png'),
  ppt: require('../../images/ppt.png'),
  pptx: require('../../images/pptx.png'),
  txt: require('../../images/txt.png'),
  odt: require('../../images/odt.png'),
  md: require('../../images/md.png'),
  rtf: require('../../images/rtf.png'),
};
const typeIcon = (name) => {
  const icon = TYPE_ICONS[(name.split('.').pop() || '').toLowerCase()];
  return icon && (icon.default || icon);
};

@observer
export class FilesCard extends React.Component {
  @observable openIndex = -1;
  @observable deleting = null;
  @observable uploading = 0;

  get files() {
    return (this.props.images || []).filter((file) => file && file.asset && file.asset.url).map((file) => {
      const name = decodeURIComponent(file.asset.url.split('?')[0].split('/').pop() || 'File');
      return {
        id: file.id,
        src: file.asset.url,
        thumbnail: (file.asset.thumb && file.asset.thumb.url) || file.asset.url,
        name,
        image: IMAGE.test(file.asset.url),
        typeIcon: typeIcon(name),
        added: [file.created_by_name, file.created_at && formatTime(file.created_at)].filter(Boolean).join(', '),
      };
    });
  }

  onPick = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    this.uploading = files.length;
    this.props.doc.uploadFiles(files)
      .then(() => { Toast.show({ message: files.length === 1 ? 'File added.' : `${files.length} files added.`, intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't add the files. Try again."), intent: Intent.DANGER }); })
      .finally(action(() => { this.uploading = 0; }));
  };

  confirmDelete = () => {
    const file = this.deleting;
    this.deleting = null;
    this.props.doc.removeFile(file)
      .then(() => { Toast.show({ message: 'File deleted.', intent: Intent.SUCCESS }); })
      .catch((error) => { Toast.show({ message: reason(error, "Couldn't delete the file. Try again."), intent: Intent.DANGER }); });
  };

  render() {
    const files = this.files;
    const images = files.filter((file) => file.image);
    const { noun } = this.props.doc;
    return (
      <SideCard
        title="Files"
        count={files.length}
        action={(
          <span className="de-upload">
            <input id="de-file-input" className="sr-only" type="file" multiple accept={FILE_ACCEPT} disabled={this.uploading > 0} onChange={this.onPick} />
            <label className="de-tool" htmlFor="de-file-input">
              <Icon icon={this.uploading ? 'time' : 'paperclip'} iconSize={14} />
              {this.uploading ? `Uploading ${this.uploading}…` : 'Add files'}
            </label>
          </span>
        )}
      >
        {files.length === 0 ? (
          <p className="de-muted">No files yet. Add photos of the job, receipts or documents for this {noun}.</p>
        ) : (
          <ul className="de-files">
            {files.map((file) => {
              const body = (
                <>
                  <span className={`de-file__thumb${file.image ? '' : ' is-type'}`}>
                    {file.image && <img src={file.thumbnail} alt="" />}
                    {!file.image && (file.typeIcon ? <img src={file.typeIcon} alt="" /> : <Icon icon="document" iconSize={18} />)}
                  </span>
                  <span className="de-file__text">
                    <strong>{file.name}</strong>
                    {file.added && <span className="de-muted">{file.added}</span>}
                  </span>
                </>
              );
              return (
                <li key={file.id} className="de-file">
                  {file.image ? (
                    <button type="button" className="de-file__open" aria-label={`Open ${file.name}`} onClick={() => { this.openIndex = images.indexOf(file); }}>
                      {body}
                    </button>
                  ) : (
                    <a className="de-file__open" href={file.src} target="_blank" rel="noopener noreferrer" aria-label={`Open ${file.name}`}>
                      {body}
                    </a>
                  )}
                  <button type="button" className="de-icon-button de-icon-button--danger de-file__delete" aria-label={`Delete ${file.name}`} title="Delete" onClick={() => { this.deleting = file; }}>
                    <Icon icon="trash" iconSize={13} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <Lightbox
          open={this.openIndex >= 0}
          close={() => { this.openIndex = -1; }}
          slides={images.map(({ src, thumbnail }) => ({ src, thumbnail }))}
          plugins={[Zoom, Thumbnails]}
          index={Math.max(this.openIndex, 0)}
        />
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete file"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>Delete this file?</strong>
          <p>It’s removed from this {noun} right away and can’t be brought back.</p>
        </Alert>
      </SideCard>
    );
  }
}

// The bar at the foot of the screen, always there: the record's figures and
// total, and saving (Discard and Save changes when there are unsaved changes;
// also ⌘S / Ctrl+S). Leaving the page with changes asks first.
@observer
export class SaveBar extends React.Component {
  @observable confirmDiscard = false;
  @observable justSaved = false;

  componentDidMount() {
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('beforeunload', this.onLeave);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('beforeunload', this.onLeave);
    clearTimeout(this.timer);
  }

  onKey = (event) => {
    if ((event.metaKey || event.ctrlKey) && (event.key === 's' || event.key === 'S')) {
      event.preventDefault();
      this.save();
    }
  };

  onLeave = (event) => {
    if (this.props.doc.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  save = () => {
    const { doc } = this.props;
    if (!doc.dirty || doc.saving) return;
    doc.save()
      .then(action(() => {
        this.justSaved = true;
        clearTimeout(this.timer);
        this.timer = setTimeout(action(() => { this.justSaved = false; }), 2500);
      }))
      .catch((error) => {
        Toast.show({ message: reason(error, `Couldn't save the ${doc.noun}. Your changes are still here; try again.`), intent: Intent.DANGER });
      });
  };

  discard = () => {
    this.confirmDiscard = false;
    this.props.doc.discard();
  };

  render() {
    const { doc, figures } = this.props;
    const state = doc.saving ? 'saving' : doc.dirty ? 'dirty' : 'saved';
    return (
      <div className={`de-savebar is-open is-${state}${this.justSaved && !doc.dirty ? ' is-just-saved' : ''}`} role="region" aria-label="Totals and saving">
        <div className="de-savebar__inner">
          {figures && <div className="de-savebar__figures">{figures}</div>}
          <div className="de-savebar__save">
            <span className="de-savebar__text" aria-live="polite">
              {doc.saving && 'Saving…'}
              {!doc.saving && doc.dirty && 'Unsaved changes'}
              {!doc.saving && !doc.dirty && (<><Icon icon="tick" iconSize={12} /> {this.justSaved ? 'Saved' : 'All changes saved'}</>)}
            </span>
            {doc.dirty && <Button minimal text="Discard" disabled={doc.saving} onClick={() => { this.confirmDiscard = true; }} />}
            <Button intent={Intent.PRIMARY} icon="floppy-disk" loading={doc.saving} disabled={!doc.dirty} onClick={this.save}>
              Save changes <kbd className="de-kbd">{/Mac/i.test(navigator.platform) ? '⌘S' : 'Ctrl+S'}</kbd>
            </Button>
          </div>
        </div>
        <Alert
          isOpen={this.confirmDiscard}
          icon="undo"
          intent={Intent.DANGER}
          cancelButtonText="Keep editing"
          confirmButtonText="Discard changes"
          onCancel={() => { this.confirmDiscard = false; }}
          onConfirm={this.discard}
        >
          <strong>Discard your changes?</strong>
          <p>The {doc.noun} goes back to how it was last saved.</p>
        </Alert>
      </div>
    );
  }
}
